import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { EventWorkflow} from '../models/Events/event-workflow';
import { environment } from 'src/environments/environment';
import { EventWorkflowListResult } from '../models/Events/event-workflow-list-result';
import { EventOverview } from '../models/Events/event-overview';
import { GetEventOverviewsResult } from '../models/Events/get-event-overviews-result';
import { GetEventResult } from '../models/Events/get-event-result';
import { EventComment } from '../models/Events/event-comment';
import { BasicResult } from '../models/basic-result';
import { UpdateEventQuestionRequest } from '../models/Events/update-event-question-request';
import { AddEventQuestionAttachmentRequest } from '../models/Events/add-event-question-attachment-request';
import { AddEventQuestionAttachmentResult } from '../models/Events/add-event-question-attachment-result';
import { RemoveEventQuestionAttachmentRequest } from '../models/Events/remove-event-question-attachment-request';
import { AddEventQuestionPhotoRequest } from '../models/Events/add-event-question-photo-request';
import { AddEventQuestionPhotoResult } from '../models/Events/add-event-question-photo-result';
import { RemoveEventQuestionPhotoRequest } from '../models/Events/remove-event-question-photo-request';
import { WorkflowTemplate } from '../models/Events/workflow-template';
import { EventHistory } from '../models/Events/event-history';
import { WorkflowActionTemplate } from '../models/Events/workflow-action-template';
import { EventWorkflowTemplatesResult } from '../models/Events/event-workflow-templates-result';
import { EventWorkflowActionsResult } from '../models/Events/event-workflow-actions-result';
import { ApplyEventWorkflowRequest } from '../models/Events/apply-event-workflow-request';
import { EventStatusItem } from '../models/Events/event-status-item';
import { QuestionAttachment } from '../models/Audits/question-attachment';
import { GetEventsFilterRequest } from '../models/Events/get-events-filter-request';
import { EventTemplateListResult } from '../models/Events/event-template-list-result';
import { EventTemplate } from '../models/Events/event-template';
import { UpsertEventTemplateRequest } from '../models/Events/upsert-event-template-request';
import { UpsertEventTemplateResult } from '../models/Events/upsert-event-template-result';
import { FlattenedQuestionHolder } from '../models/Audits/flattened-question-holder';

@Injectable({
  providedIn: 'root'
})
export class EventsService {

  constructor(private httpClient: HttpClient) { }
  
  
    httpOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      observe: 'response' as 'response',
      responseType: 'json' as const,
      withCredentials: true,
    };

    getWorkflows():Observable<EventWorkflow[]>{
      return this.httpClient.get<EventWorkflowListResult>(`${environment.apiUrl}events/getworkflows`, this.httpOptions).pipe(
        map(res => {
          if (!res.ok){
            return [] as EventWorkflow[];
          }
          if (res.body?.IsError){
            return [] as EventWorkflow[];
          } else {
            return (res.body as EventWorkflowListResult).EventWorkflows;
          }
        })
      );
    }

    getTopLevelEvents(filter?: GetEventsFilterRequest):Observable<EventOverview[]>{
      const payload = filter ?? new GetEventsFilterRequest();
      return this.httpClient.post<GetEventOverviewsResult>(`${environment.apiUrl}events/getevents`, payload, this.httpOptions).pipe(
        map(res => {
          if (!res.ok){
            return [] as EventOverview[];
          }
          if (res.body?.IsError){
            return [] as EventOverview[];
          }
          return (res.body as GetEventOverviewsResult).EventOverviews;
        })
      );
    }

    getEvent(EventID: number):Observable<GetEventResult>{
      return this.httpClient.post<GetEventResult>(`${environment.apiUrl}events/getevent`, EventID,this.httpOptions).pipe(
        map(res => {
          if (!res.ok){
            return new GetEventResult({IsError:true, ErrorCode: 'LOCAL'});
          }
          if (res.body?.IsError){
            return res.body!;
          }
          return res.body!;
        })
      );
    }

    getEventComments(eventId: number): Observable<EventComment[]>{
      return this.httpClient.post<any>(`${environment.apiUrl}events/getcomments`, eventId, this.httpOptions).pipe(
        map(res => {
          const body = res?.body ?? res;
          if (body?.IsError){
            return [] as EventComment[];
          }
          return (body?.Comments || []).map((c:any) => new EventComment({
            EventCommentID: c.EventCommentID,
            EventID: c.EventID,
            Comment: c.EventCommentText ?? c.EventComment,
            DateCreated: c.DateCreated,
            UserCreated: c.UserCreated,
            UserDisplayName: c.UserDisplayName
          }));
        })
      );
    }

    addEventComment(eventId: number, comment: string): Observable<BasicResult>{
      const payload = { EventID: eventId, Comment: comment };
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}events/addcomment`, payload, this.httpOptions).pipe(
        map((res: any) => {
          const body = res?.body ?? res;
          return body as BasicResult;
        })
      );
    }

    updateEventQuestion(request: UpdateEventQuestionRequest): Observable<BasicResult>{
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}events/updatequestion`, request, this.httpOptions).pipe(
        map((res: any) => {
          const body = res?.body ?? res;
          return body as BasicResult;
        })
      );
    }

    addEventQuestionAttachment(request: AddEventQuestionAttachmentRequest): Observable<AddEventQuestionAttachmentResult>{
      return this.httpClient.post<AddEventQuestionAttachmentResult>(`${environment.apiUrl}events/addquestionattachment`, request, this.httpOptions).pipe(
        map((res: any) => res?.body ?? res)
      );
    }

    removeEventQuestionAttachment(request: RemoveEventQuestionAttachmentRequest): Observable<BasicResult>{
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}events/removequestionattachment`, request, this.httpOptions).pipe(
        map((res: any) => res?.body ?? res)
      );
    }

    addEventQuestionPhoto(request: AddEventQuestionPhotoRequest): Observable<AddEventQuestionPhotoResult>{
      return this.httpClient.post<AddEventQuestionPhotoResult>(`${environment.apiUrl}events/addquestionphoto`, request, this.httpOptions).pipe(
        map((res: any) => res?.body ?? res)
      );
    }

    removeEventQuestionPhoto(request: RemoveEventQuestionPhotoRequest): Observable<BasicResult>{
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}events/removequestionphoto`, request, this.httpOptions).pipe(
        map((res: any) => res?.body ?? res)
      );
    }

    getEventWorkflows(eventId: number): Observable<WorkflowTemplate[]>{
      return this.httpClient.get<EventWorkflowTemplatesResult>(`${environment.apiUrl}events/${eventId}/workflows`, this.httpOptions).pipe(
        map((res: any) => {
          const body = res?.body ?? res;
          if (body?.IsError){
            return [] as WorkflowTemplate[];
          }
          return body?.Workflows ?? [];
        })
      );
    }

    getEventWorkflowActions(eventId: number, workflowId: number): Observable<WorkflowActionTemplate[]>{
      return this.httpClient.get<EventWorkflowActionsResult>(`${environment.apiUrl}events/${eventId}/workflows/${workflowId}`, this.httpOptions).pipe(
        map((res: any) => {
          const body = res?.body ?? res;
          if (body?.IsError){
            return [] as WorkflowActionTemplate[];
          }
          return body?.Actions ?? [];
        })
      );
    }

    applyEventWorkflow(request: ApplyEventWorkflowRequest): Observable<BasicResult>{
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}events/${request.EventID}/workflows/apply`, request, this.httpOptions).pipe(
        map((res: any) => res?.body ?? res)
      );
    }

    getEventStatuses(): Observable<EventStatusItem[]>{
      return this.httpClient.get<EventStatusItem[]>(`${environment.apiUrl}events/statuses`, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? [])
      );
    }

    updateEventStatus(eventId: number, statusId: number): Observable<BasicResult>{
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}events/${eventId}/status`, statusId, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res)
      );
    }

    getEventAttachments(eventId: number): Observable<QuestionAttachment[]>{
      return this.httpClient.get<QuestionAttachment[]>(`${environment.apiUrl}events/${eventId}/attachments`, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? [])
      );
    }

    uploadEventAttachment(eventId: number, file: File){
      const fd = new FormData();
      fd.append('file', file);
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}events/${eventId}/attachments`, fd, {
        withCredentials: true,
        reportProgress: true,
        observe: 'events'
      });
    }

    downloadEventAttachment(guid: string){
      return this.httpClient.get(`${environment.apiUrl}events/attachments/${guid}`, {
        observe: 'response',
        responseType: 'blob',
        withCredentials: true
      });
    }

    deleteEventAttachment(eventId: number, guid: string): Observable<BasicResult>{
      return this.httpClient.delete<BasicResult>(`${environment.apiUrl}events/attachments/${guid}?eventId=${eventId}`, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res)
      );
    }

    getEventHistory(eventId: number): Observable<EventHistory[]>{
      return this.httpClient.get<EventHistory[]>(`${environment.apiUrl}events/${eventId}/history`, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? [])
      );
    }

    getEventTemplates(): Observable<EventTemplateListResult>{
      return this.httpClient.get<EventTemplateListResult>(`${environment.apiUrl}event-templates`, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? new EventTemplateListResult())
      );
    }

    getAvailableEventTemplates(): Observable<EventTemplateListResult>{
      return this.httpClient.get<EventTemplateListResult>(`${environment.apiUrl}event-templates/available`, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? new EventTemplateListResult())
      );
    }

    saveEventTemplate(request: UpsertEventTemplateRequest): Observable<UpsertEventTemplateResult>{
      return this.httpClient.post<UpsertEventTemplateResult>(`${environment.apiUrl}event-templates/save`, request, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? new UpsertEventTemplateResult())
      );
    }

    deleteEventTemplate(eventWorkflowID: number): Observable<BasicResult>{
      const payload = { eventWorkflowID };
      return this.httpClient.post<BasicResult>(`${environment.apiUrl}event-templates/delete`, payload, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? new BasicResult())
      );
    }

    getEventTemplateAudits(): Observable<FlattenedQuestionHolder[]>{
      return this.httpClient.get<FlattenedQuestionHolder[]>(`${environment.apiUrl}event-templates/audits`, this.httpOptions).pipe(
        map((res:any) => res?.body ?? res ?? [])
      );
    }
}
